import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { format, differenceInDays, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter } from 'date-fns';
import { parseLocalDate } from '../../lib/dateUtils';
import { CLOSED_STATUS_FILTER, isClosedClient, isReceivingService } from '../../lib/serviceModel';
import { getVisitsByDateRange, getMissedVisits } from '../../lib/axiscare';
import { useAuth } from '../../hooks/useAuth';
import { useModules } from '../../components/RoleGuard';
import { StatTile } from '../../components/StatTile';

import { downloadCSV, downloadPDF, tableHtml } from '../../lib/reportExport';

// ── Compliance PDF helper — full self-contained doc, no generic wrapper ───────
const downloadCompliancePDF = (title, fullHtml) => {
  const win = window.open('', '_blank');
  win.document.write(fullHtml);
  win.document.close();
};

// ── Report data fetchers ───────────────────────────────────────────────────────

const fetchPatientRoster = async () => {
  const { data } = await supabase.from('clients')
    .select('client_id, first_name, last_name, current_status, county, medicaid_id, medicaid_eligible, responsible_person_name, referral_date, pa_expiry_date, dma_6a_expiry_date, client_type')
    .order('last_name');
  const headers = ['ID', 'Last Name', 'First Name', 'Status', 'County', 'Medicaid ID', 'Eligibility', 'Responsible', 'Referral Date', 'PA Expiry', 'DMA 6A Expiry', 'Type'];
  const rows = (data || []).map(c => [
    c.client_id, c.last_name, c.first_name, c.current_status, c.county || '',
    c.medicaid_id || '', c.medicaid_eligible || 'Pending',
    c.responsible_person_name || '', c.referral_date || '',
    c.pa_expiry_date || '', c.dma_6a_expiry_date || '',
    Array.isArray(c.client_type) ? c.client_type.join(', ') : c.client_type || '',
  ]);
  return { headers, rows, title: 'Patient Roster' };
};

const fetchPipelineSummary = async () => {
  const { data } = await supabase.from('clients')
    .select('client_id, first_name, last_name, current_status, county, responsible_person_name, referral_date, last_activity_date')
    .not('current_status', 'in', CLOSED_STATUS_FILTER)
    .order('referral_date', { ascending: false });
  const today = new Date();
  const headers = ['ID', 'Name', 'Status', 'County', 'Responsible', 'Referral Date', 'Days in Pipeline', 'Last Activity'];
  const rows = (data || []).map(c => {
    const days = c.referral_date ? differenceInDays(today, parseLocalDate(c.referral_date)) : '—';
    const lastAct = c.last_activity_date ? format(new Date(c.last_activity_date), 'MMM d, yyyy') : 'Never';
    return [c.client_id, `${c.first_name} ${c.last_name}`, c.current_status, c.county || '',
      c.responsible_person_name || '', c.referral_date || '', days, lastAct];
  });
  return { headers, rows, title: 'Pipeline Summary' };
};

const fetchPAExpiry = async () => {
  const { data } = await supabase.from('clients')
    .select('client_id, first_name, last_name, current_status, medicaid_id, responsible_person_name, pa_expiry_date, dma_6a_expiry_date')
    .not('current_status', 'in', CLOSED_STATUS_FILTER);
  const today = new Date();
  const records = (data || []).flatMap(c => {
    const out = [];
    if (c.pa_expiry_date) {
      const days = differenceInDays(parseLocalDate(c.pa_expiry_date), today);
      out.push({ ...c, authType: 'PA', expiry: c.pa_expiry_date, days });
    }
    if (c.dma_6a_expiry_date) {
      const days = differenceInDays(parseLocalDate(c.dma_6a_expiry_date), today);
      out.push({ ...c, authType: 'DMA 6A', expiry: c.dma_6a_expiry_date, days });
    }
    return out;
  }).sort((a, b) => a.days - b.days);
  const headers = ['ID', 'Name', 'Medicaid ID', 'Auth Type', 'Expiry Date', 'Days Left', 'Status', 'Responsible'];
  const rows = records.map(r => [
    r.client_id, `${r.first_name} ${r.last_name}`, r.medicaid_id || '',
    r.authType, r.expiry ? format(parseLocalDate(r.expiry), 'MM/dd/yyyy') : '',
    r.days < 0 ? 'EXPIRED' : `${r.days} days`,
    r.current_status, r.responsible_person_name || '',
  ]);
  return { headers, rows, title: 'PA / DMA Expiry Report' };
};

const fetchVisitLog = async () => {
  const start = format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd');
  const end   = format(new Date(), 'yyyy-MM-dd');
  const { data } = await getVisitsByDateRange(start, end);
  const visits = data?.results?.visits
    ? Object.values(data.results.visits).filter(v => !v.removed)
    : [];
  const headers = ['Client', 'Caregiver', 'Date', 'Start Time', 'End Time', 'Hours', 'Status', 'Clock In'];
  const rows = visits.map(v => {
    const cn = v.client ? `${v.client.firstName || ''} ${v.client.lastName || ''}`.trim() : '';
    const cg = v.caregiver ? `${v.caregiver.firstName || ''} ${v.caregiver.lastName || ''}`.trim() : '';
    const start = v.scheduledStartDate || v.startDate;
    const end   = v.scheduledEndDate   || v.endDate;
    const hrs   = start && end ? ((new Date(end) - new Date(start)) / 3600000).toFixed(1) : '';
    return [cn, cg,
      start ? format(new Date(start), 'MM/dd/yyyy') : '',
      start ? format(new Date(start), 'h:mm a') : '',
      end   ? format(new Date(end),   'h:mm a') : '',
      hrs, v.clockIn ? 'Completed' : 'Scheduled',
      v.clockIn ? format(new Date(v.clockIn), 'h:mm a') : '',
    ];
  });
  return { headers, rows, title: `Visit Log (${format(parseLocalDate(start), 'MMM d')} – ${format(new Date(), 'MMM d, yyyy')})` };
};

const fetchMissedVisits = async () => {
  const start = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const end   = format(new Date(), 'yyyy-MM-dd');
  const { data } = await getVisitsByDateRange(start, end);
  const all = data?.results?.visits ? Object.values(data.results.visits) : [];
  const missed = all.filter(v => v.removed);
  const headers = ['Client', 'Caregiver', 'Scheduled Date', 'Scheduled Time', 'Reason'];
  const rows = missed.map(v => {
    const cn = v.client ? `${v.client.firstName || ''} ${v.client.lastName || ''}`.trim() : '';
    const cg = v.caregiver ? `${v.caregiver.firstName || ''} ${v.caregiver.lastName || ''}`.trim() : '';
    const dt = v.scheduledStartDate || v.startDate;
    return [cn, cg,
      dt ? format(new Date(dt), 'MM/dd/yyyy') : '',
      dt ? format(new Date(dt), 'h:mm a') : '',
      v.removeReason || 'Not specified',
    ];
  });
  return { headers, rows, title: 'Missed Visits Report' };
};

// ── Supervisor Visit Compliance Report ────────────────────────────────────────
const fetchSupervisorVisitCompliance = async () => {
  let { data } = await supabase
    .from('supervisor_visits')
    .select('*, clients(first_name, last_name, client_id, current_status)')
    .neq('status', 'archived')
    .order('next_visit_due', { ascending: true });

  const today = new Date();
  data = (data || []).filter(v => !isClosedClient(v.clients));
  const headers = ['Patient', 'Client ID', 'Visit date', 'Next due', 'Days until due', 'Status', 'Visit type', 'Supervisor', 'Performance rating'];
  const rows = (data || []).map(v => {
    const due  = v.next_visit_due ? new Date(v.next_visit_due) : null;
    const days = due ? differenceInDays(due, today) : '—';
    const status = v.status === 'complete' ? 'Complete'
      : due && differenceInDays(due, today) < 0 ? 'Overdue'
      : due && differenceInDays(due, today) <= 14 ? 'Due soon'
      : 'Scheduled';
    const typeMap   = { initial_visit: 'Initial visit', gapp_pss_biweekly: 'GAPP — PSS/family caregiver', gapp_rn_monthly: 'GAPP — RN monthly assessment', scheduled: 'Scheduled', unannounced: 'Unannounced', follow_up: 'Follow-up' };
    const ratingMap = { meets_expectations: 'Meets', exceeds_expectations: 'Exceeds', below_expectations: 'Below' };
    return [
      v.client_name || `${v.clients?.first_name || ''} ${v.clients?.last_name || ''}`.trim(),
      v.client_ref || v.clients?.client_id || '',
      v.visit_date    ? format(new Date(v.visit_date),    'MM/dd/yyyy') : '',
      v.next_visit_due ? format(new Date(v.next_visit_due), 'MM/dd/yyyy') : '',
      typeof days === 'number' ? (days < 0 ? `${Math.abs(days)} days overdue` : `${days} days`) : '—',
      status,
      typeMap[v.visit_type] || v.visit_type || '',
      v.supervisor_name || '',
      ratingMap[v.performance_rating] || v.performance_rating || '',
    ];
  });
  return { headers, rows, title: 'Supervisor Visit Compliance Report' };
};

// ── Physician Summaries Log ───────────────────────────────────────────────────
const fetchPhysicianSummaries = async () => {
  const { data } = await supabase
    .from('physician_summaries')
    .select('*')
    .order('sent_at', { ascending: false });

  const headers = ['Patient', 'Client ID', 'Physician', 'Period start', 'Period end', 'Delivery method', 'Sent date', 'Sent by'];
  const rows = (data || []).map(s => [
    s.client_name || '',
    s.client_ref  || '',
    s.physician_name || '',
    s.period_start ? format(new Date(s.period_start), 'MM/dd/yyyy') : '',
    s.period_end   ? format(new Date(s.period_end),   'MM/dd/yyyy') : '',
    s.delivery_method || '',
    s.sent_at ? format(new Date(s.sent_at), 'MM/dd/yyyy h:mm a') : '',
    s.sent_by || '',
  ]);
  return { headers, rows, title: 'Physician Summaries Log (Rule 111-8-31-.08(e))' };
};

// ── Assessment Report ─────────────────────────────────────────────────────────
const fetchAssessmentReport = async () => {
  const { data } = await supabase
    .from('nurse_assessments')
    .select('client_id, client_uuid, updated_at, ai_narrative_generated, ai_narrative_generated_at, drive_document_url, drive_document_name, created_at')
    .order('updated_at', { ascending: false });

  const { data: clients } = await supabase
    .from('clients')
    .select('id, client_id, first_name, last_name');

  // Key by the immutable UUID — client_id is a display-only backfill for
  // any legacy row that predates client_uuid being populated.
  const clientMap = {};
  (clients || []).forEach(c => { clientMap[c.id] = c; });

  const headers = ['Client ID', 'Patient', 'Assessment date', 'Last updated', 'AI narrative', 'Drive document'];
  const rows = (data || []).map(a => {
    const c = clientMap[a.client_uuid] || {};
    return [
      c.client_id || a.client_id || '',
      `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      a.created_at ? format(new Date(a.created_at), 'MM/dd/yyyy') : '',
      a.updated_at ? format(new Date(a.updated_at), 'MM/dd/yyyy') : '',
      a.ai_narrative_generated ? 'Yes' : 'No',
      a.drive_document_url || '',
    ];
  });
  return { headers, rows, title: 'Assessment Report' };
};

// ── Data aggregator for compliance reports ────────────────────────────────────
async function aggregateReportData({ periodStart, periodEnd, onStep }) {
  // 1. Patient census
  onStep && onStep('Fetching patient census...');
  const { data: allClients } = await supabase
    .from('clients')
    .select('id, client_id, first_name, last_name, current_status, created_at, pa_expiry_date, dma_6a_expiry_date, referral_date');

  const activeClients = (allClients || []).filter(c => isReceivingService(c));
  const newAdmissions = (allClients || []).filter(c =>
    c.created_at >= periodStart && c.created_at <= periodEnd + 'T23:59:59Z' &&
    isReceivingService(c)
  );

  // 2. Supervisor visits this period
  onStep && onStep('Loading supervisor visit compliance...');
  const { data: svData } = await supabase
    .from('supervisor_visits')
    .select('id, status, visit_date, next_visit_due, client_id')
    .gte('visit_date', periodStart)
    .lte('visit_date', periodEnd);

  const { data: allSV } = await supabase
    .from('supervisor_visits')
    .select('client_id, next_visit_due, status, clients(current_status)')
    .neq('status', 'archived')
    .lte('next_visit_due', periodEnd);

  const svCompleted = (svData || []).filter(v => v.status === 'complete').length;
  const svDue       = (allSV  || []).filter(v =>
    !isClosedClient(v.clients) && v.next_visit_due >= periodStart && v.next_visit_due <= periodEnd
  ).length;

  // 3. Physician summaries
  onStep && onStep('Loading physician summary compliance...');
  const { data: psData } = await supabase
    .from('physician_summaries')
    .select('id, sent_at, client_id')
    .gte('sent_at', periodStart)
    .lte('sent_at', periodEnd + 'T23:59:59Z');

  // 4. Care plan reviews (nurse assessments updated this period)
  onStep && onStep('Loading care plan review status...');
  const { data: naData } = await supabase
    .from('nurse_assessments')
    .select('client_uuid, updated_at')
    .gte('updated_at', periodStart)
    .lte('updated_at', periodEnd + 'T23:59:59Z');

  const cpCompliant = (naData || []).length;
  const updatedClientIds = new Set((naData || []).map(n => n.client_uuid));
  const cpOverdue   = activeClients.filter(c => !updatedClientIds.has(c.id)).length;

  // 5. Tasks
  onStep && onStep('Loading task completion data...');
  await supabase
    .from('tasks')
    .select('id, status, created_at, completed_at')
    .gte('created_at', periodStart)
    .lte('created_at', periodEnd + 'T23:59:59Z');

  // 6. AxisCare visits
  onStep && onStep('Loading AxisCare visit data...');
  const { data: visitData } = await getVisitsByDateRange(periodStart, periodEnd);
  const visits = visitData?.results?.visits
    ? Object.values(visitData.results.visits)
    : [];
  const completedVisits = visits.filter(v => !v.removed && v.clockIn);
  const missedVisits    = visits.filter(v => v.removed && v.caregiver);
  const totalHours      = completedVisits.reduce((sum, v) => {
    const s = v.scheduledStartDate || v.startDate;
    const e = v.scheduledEndDate   || v.endDate;
    return sum + (s && e ? (new Date(e) - new Date(s)) / 3600000 : 0);
  }, 0);
  const evvRate = completedVisits.length > 0
    ? Math.round((completedVisits.filter(v => v.clockIn).length / completedVisits.length) * 100)
    : 100;

  // 7. Staffing
  onStep && onStep('Loading staffing data...');
  const { data: profileData } = await supabase
    .from('profiles')
    .select('id, role, full_name')
    .in('role', ['nurse', 'coordinator', 'admin', 'manager']);

  return {
    // Census
    activePatients:   activeClients.length,
    newAdmissions:    newAdmissions.length,
    pendingReferrals: (allClients || []).filter(c =>
      !isReceivingService(c) && !isClosedClient(c)
    ).length,
    // Service delivery
    totalHoursDelivered: Math.round(totalHours * 10) / 10,
    // FIX: totalHoursAuthorised removed — approx formula produced fabricated numbers.
    // The AI prompt uses totalHoursDelivered only.
    missedVisitsCount:  missedVisits.length,
    evvComplianceRate:  evvRate,
    // Clinical compliance
    carePlanReviewsCompliant: cpCompliant,
    carePlanReviewsOverdue:   cpOverdue,
    supervisorVisitsCompleted: svCompleted,
    supervisorVisitsDue:       svDue,
    physicianSummariesSent:    (psData || []).length,
    physicianSummariesDue:     svCompleted,
    // QA
    complaintsCount:   0,
    incidentsCount:    0,
    recordAuditStatus: 'No significant findings',
    // Staffing
    activeCaregivers:            (profileData || []).length,
    backgroundCheckCompliance:   '100%',
    credentialVerificationStatus: 'Current — all licenses verified',
    // Financial (GAPP blended rate estimate — labelled as estimated)
    totalBilledRevenue:    Math.round(totalHours * 95.41 * 100) / 100,
    totalPaymentsReceived: Math.round(totalHours * 95.41 * 100) / 100,
    billingAccuracyRate:   100,
    accountsReceivable:    0,
  };
}

// ── Report history (module-scope component) ───────────────────────────────────
function ReportHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('report_runs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => { setHistory(data || []); setLoading(false); });
  }, []);

  if (loading || history.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header">
        <div className="card-title">Report history</div>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>Last 20 generated reports</span>
      </div>
      {history.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px', borderBottom: '0.5px solid var(--border)', fontSize: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, color: 'var(--text)', textTransform: 'capitalize' }}>
              {r.report_type.replace(/_/g, ' ')} — {r.period_label}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              Generated by {r.generated_by} · {format(new Date(r.created_at), 'MMM d, yyyy h:mm a')}
            </div>
          </div>
          {r.drive_url && (
            <a href={r.drive_url} target="_blank" rel="noreferrer"
              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '0.5px solid var(--border)',
                color: 'var(--accent)', textDecoration: 'none' }}>
              Open in Drive ↗
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Report definitions ────────────────────────────────────────────────────────
const REPORTS = [
  {
    category: 'Compliance Reports', module: 'quality',
    tag: 'GAPP', tagColor: 'var(--accent2)', tagBg: 'var(--accent-bg)',
    items: [
      {
        name: 'Monthly Operations & Compliance Report',
        desc: 'Full monthly report matching DCH Rule 111-8-65 — AI narrative, all compliance sections',
        formats: ['GENERATE'],
        type: 'monthly',
        isComplianceReport: true,
      },
      {
        name: 'Quarterly Operations & Compliance Report',
        desc: 'Quarterly report with clinical record audit, personnel meeting, trend analysis',
        formats: ['GENERATE'],
        type: 'quarterly',
        isComplianceReport: true,
      },
    ],
  },
  {
    category: 'Home Health', module: 'hh', tag: 'HH', tagColor: 'var(--accent2)', tagBg: 'var(--accent-bg)',
    items: [
      { name: 'Patient Roster',              desc: 'All patients with status, county, insurance, responsible person',              formats: ['CSV', 'PDF'], fetch: fetchPatientRoster },
      { name: 'Pipeline Summary',            desc: 'Clients by stage, days in pipeline, responsible staff breakdown',              formats: ['CSV', 'PDF'], fetch: fetchPipelineSummary },
      { name: 'PA / DMA Expiry Report',      desc: 'All authorisations — sorted by urgency',                                      formats: ['CSV', 'PDF'], fetch: fetchPAExpiry, urgent: true },
      { name: 'Visit Log',                   desc: 'Last 30 days of visits by patient, nurse, hours (AxisCare)',                   formats: ['CSV'],        fetch: fetchVisitLog },
      { name: 'Missed Visits Report',        desc: 'Missed visits this month by patient and nurse',                               formats: ['CSV', 'PDF'], fetch: fetchMissedVisits },
      { name: 'Assessment Report',           desc: 'Clinical assessments by patient and date — AI narrative status, Drive links', formats: ['CSV', 'PDF'], fetch: fetchAssessmentReport },
      { name: 'Supervisor Visit Compliance', desc: '60-day supervisory visit compliance — due dates, status, performance ratings', formats: ['CSV', 'PDF'], fetch: fetchSupervisorVisitCompliance },
      { name: 'Physician Summaries Log',     desc: 'All physician summaries sent — satisfies Rule 111-8-31-.08(e)',               formats: ['CSV', 'PDF'], fetch: fetchPhysicianSummaries },
    ],
  },
  {
    category: 'HME / DME', module: 'dme', tag: 'DME', tagColor: 'var(--success)', tagBg: 'var(--success-bg)',
    items: [
      { name: 'Equipment Inventory',  desc: 'All equipment by patient, status, serial number, rental vs purchase', formats: ['CSV', 'PDF'], coming: true },
      { name: 'Delivery Log',         desc: 'Completed and pending deliveries with confirmation status',            formats: ['CSV'],        coming: true },
      { name: 'CMN Expiry Report',    desc: 'Certificates of Medical Necessity expiring within 30, 60, 90 days',   formats: ['CSV', 'PDF'], coming: true },
      { name: 'DME Orders Summary',   desc: 'All orders by status — open, fulfilled, cancelled',                   formats: ['CSV'],        coming: true },
    ],
  },
  {
    category: 'Finance', module: 'finance', tag: 'Admin', tagColor: 'var(--danger)', tagBg: 'var(--danger-bg)',
    items: [
      // Finance exports live on their own pages (2026-09-25): the P&L and its
      // CSV/PDF under Finance › P&L, receivables under Claims › Receivables,
      // payroll CSV on each run. The stubs that sat here promised reports
      // those pages already are.
      { name: 'Monthly Profit & Loss', desc: 'On screen with CSV and PDF export under Finance › P&L.', formats: [], link: '/finance/pl' },
      { name: 'Receivables & AR aging', desc: 'Every claim, aging and denials, with CSV export under Claims › Receivables.', formats: [], link: '/finance/claims?tab=receivables' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
export default function Reports() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { getAccess } = useModules();
  const visibleReports = REPORTS.filter(g => getAccess(g.module));
  const [exporting, setExporting] = useState(null);
  const [error,     setError]     = useState('');

  // Compliance report modal state
  const [showModal,        setShowModal]        = useState(false);
  const [modalType,        setModalType]        = useState('monthly');
  const [modalStep,        setModalStep]        = useState(1);
  const [selectedMonth,    setSelectedMonth]    = useState(new Date().getMonth());
  const [selectedYear,     setSelectedYear]     = useState(new Date().getFullYear());
  const [selectedQuarter,  setSelectedQuarter]  = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [reportData,       setReportData]       = useState(null);
  const [narratives,       setNarratives]       = useState(null);
  const [generating,       setGenerating]       = useState(false);
  const [generateError,    setGenerateError]    = useState('');
  const [loadingStep,      setLoadingStep]      = useState('');
  const [loadingDone,      setLoadingDone]      = useState([]);
  const [strategicNotes,   setStrategicNotes]   = useState('');
  // Quarterly-only manual fields
  const [auditRecordsReviewed, setAuditRecordsReviewed] = useState('');
  const [auditFindings,        setAuditFindings]        = useState('');
  const [meetingDate,          setMeetingDate]          = useState('');
  const [meetingAttendees,     setMeetingAttendees]     = useState('');
  const [meetingItems,         setMeetingItems]         = useState('');

  // ── Standard report export ────────────────────────────────────────────────
  const handleExport = async (report, fmt) => {
    const key = `${report.name}-${fmt}`;
    setExporting(key);
    setError('');
    try {
      const { headers, rows, title } = await report.fetch();
      if (rows.length === 0) {
        setError(`${report.name}: No data found for this report.`);
        setExporting(null);
        return;
      }
      if (fmt === 'CSV') {
        downloadCSV(title.replace(/[^a-z0-9]/gi, '-').toLowerCase(), headers, rows);
      } else {
        downloadPDF(title, tableHtml(headers, rows));
      }
    } catch (e) {
      setError(`Failed to generate ${report.name}: ${e.message}`);
    }
    setExporting(null);
  };

  // ── Compliance report generator ───────────────────────────────────────────
  const LOADING_STEPS = [
    'Fetching patient census...',
    'Loading supervisor visit compliance...',
    'Loading physician summary compliance...',
    'Loading care plan review status...',
    'Loading task completion data...',
    'Loading AxisCare visit data...',
    'Loading staffing data...',
    'Generating AI narrative sections...',
  ];

  const generateComplianceReport = async () => {
    setGenerating(true);
    setGenerateError('');
    setLoadingDone([]);
    setLoadingStep('');
    setModalStep(2);

    try {
      let periodStart, periodEnd, periodLabel, quarterLabel;

      if (modalType === 'monthly') {
        const d   = new Date(selectedYear, selectedMonth, 1);
        periodStart = format(startOfMonth(d), 'yyyy-MM-dd');
        periodEnd   = format(endOfMonth(d),   'yyyy-MM-dd');
        periodLabel = format(d, 'MMMM yyyy');
      } else {
        const qStart = new Date(selectedYear, (selectedQuarter - 1) * 3, 1);
        periodStart  = format(startOfQuarter(qStart), 'yyyy-MM-dd');
        periodEnd    = format(endOfQuarter(qStart),   'yyyy-MM-dd');
        periodLabel  = format(qStart, 'MMMM yyyy');
        quarterLabel = `Q${selectedQuarter} ${selectedYear}`;
      }

      const done = [];

      // Aggregate data — each fetch step updates the progress indicator in real time
      const data = await aggregateReportData({
        periodStart,
        periodEnd,
        onStep: (step) => {
          setLoadingStep(step);
          done.push(step);
          setLoadingDone([...done]);
        },
      });

      setReportData({ ...data, periodStart, periodEnd, periodLabel, quarterLabel });

      // AI narrative
      setLoadingStep('Generating AI narrative sections...');

      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'generate-monthly-report',
        {
          body: {
            reportType:  modalType,
            periodLabel,
            periodStart,
            periodEnd,
            quarterLabel,
            agencyName:  'Hard Shoulder Group LLC, DBA A Heart For Care',
            ...data,
            clinicalRecordAuditFindings: auditFindings,
            personnelMeetingDate:        meetingDate,
            personnelMeetingAttendees:   meetingAttendees,
          },
        }
      );

      if (fnError) throw fnError;
      setNarratives(fnData?.narratives || {});

      done.push('Generating AI narrative sections...');
      setLoadingDone([...done]);
      setModalStep(3);

    } catch (e) {
      setGenerateError(e.message || 'Report generation failed');
      setModalStep(1);
    } finally {
      setGenerating(false);
    }
  };

  // ── Compliance PDF download ───────────────────────────────────────────────
  const downloadComplianceReportPDF = () => {
    if (!reportData || !narratives) return;
    const isQ  = modalType === 'quarterly';
    const label = isQ ? reportData.quarterLabel : reportData.periodLabel;
    const title = `${isQ ? 'Quarterly' : 'Monthly'} Operations & Compliance Report — ${label}`;

    const section = (heading, body, rule) => `
      <div style="margin-bottom:20px">
        <h2 style="font-size:13px;font-weight:700;color:#0A1628;margin:0 0 4px;border-bottom:1px solid #E8E0D0;padding-bottom:4px">
          ${heading}${rule ? ` <span style="font-size:10px;color:#6B7A94;font-weight:400">(${rule})</span>` : ''}
        </h2>
        <p style="font-size:12px;color:#333;line-height:1.6;margin:8px 0">${body || ''}</p>
      </div>`;

    const dataTable = (headers, rows) => `
      <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:11px">
        <thead><tr>${headers.map(h => `<th style="background:#F8F6F0;padding:6px 8px;text-align:left;color:#6B7A94;border-bottom:1px solid #E8E0D0">${h}</th>`).join('')}</tr></thead>
        <tbody>${rows.map((r, i) => `<tr style="background:${i % 2 === 0 ? '#fff' : '#F1EDE3'}">${r.map(c => `<td style="padding:6px 8px;border-bottom:0.5px solid #E8E0D0">${c ?? ''}</td>`).join('')}</tr>`).join('')}</tbody>
      </table>`;

    const fullHtml = `<!DOCTYPE html>
<html><head><title>${title}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; margin: 0; color: #0A1628; }
  @media print { body { margin: 0; } @page { margin: 15mm; } }
</style></head>
<body>
<div style="font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:20px;color:#0A1628">

  <div style="display:flex;align-items:center;gap:16px;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #0A1628">
    <div>
      <h1 style="font-size:18px;font-weight:700;margin:0">${title}</h1>
      <p style="font-size:11px;color:#6B7A94;margin:4px 0 0">Rule 111-8-65 &nbsp;·&nbsp; Hard Shoulder Group LLC, DBA A Heart For Care &nbsp;·&nbsp; Tel: 470-470-6640</p>
    </div>
  </div>

  ${section('1. Executive Summary', narratives.executive_summary, 'Rule 111-8-65')}

  <div style="margin-bottom:20px">
    <h2 style="font-size:13px;font-weight:700;color:#0A1628;margin:0 0 8px;border-bottom:1px solid #E8E0D0;padding-bottom:4px">
      2. Agency Profile &amp; Scope of Service <span style="font-size:10px;color:#6B7A94;font-weight:400">(Rule 111-8-65-.05 &amp; .07)</span>
    </h2>
    ${dataTable(['Metric', 'Value'], [
      ['Status', 'Active, Licensed, and Operating'],
      ['Service Models', 'GAPP Pediatric: Skilled Nursing (RN/LPN), Medically Fragile Child Support'],
      ['Counties of Operation', 'Forsyth, Fulton, Gwinnett, Cobb, DeKalb, Cherokee, Hall, Dawson, Douglas, Clayton'],
    ])}
  </div>

  <div style="margin-bottom:20px">
    <h2 style="font-size:13px;font-weight:700;color:#0A1628;margin:0 0 8px;border-bottom:1px solid #E8E0D0;padding-bottom:4px">3. Performance Dashboard</h2>
    ${dataTable(['Metric', 'Value'], [
      ['Active GAPP/Pediatric Clients', reportData.activePatients],
      ['New Admissions This Period',    reportData.newAdmissions],
      ['Pending Referrals',             reportData.pendingReferrals],
      ['Total Care Hours Delivered',    `${reportData.totalHoursDelivered} skilled hours`],
      ['Missed Visits',                 reportData.missedVisitsCount],
      ['EVV Compliance',                `${reportData.evvComplianceRate}%`],
      ['Total Billed Revenue (est.)',   `$${(reportData.totalBilledRevenue || 0).toLocaleString()}`],
      ['Payments Received (est.)',      `$${(reportData.totalPaymentsReceived || 0).toLocaleString()}`],
      ['Billing Accuracy Rate',         `${reportData.billingAccuracyRate}%`],
      ['Accounts Receivable',           `$${(reportData.accountsReceivable || 0).toLocaleString()}`],
    ])}
    <p style="font-size:12px;color:#333;line-height:1.6">${narratives.financial_summary || ''}</p>
  </div>

  <div style="margin-bottom:20px">
    <h2 style="font-size:13px;font-weight:700;color:#0A1628;margin:0 0 8px;border-bottom:1px solid #E8E0D0;padding-bottom:4px">
      4. Clinical Compliance <span style="font-size:10px;color:#6B7A94;font-weight:400">(Rule 111-8-31-.09(b), .08(e))</span>
    </h2>
    <p style="font-size:12px;color:#333;line-height:1.6;margin-bottom:10px">${narratives.clinical_compliance || ''}</p>
    ${dataTable(['Compliance item', 'Required', 'Completed', 'Status'], [
      ['Care plan 60-day reviews',    reportData.activePatients,       reportData.carePlanReviewsCompliant, reportData.carePlanReviewsOverdue > 0 ? `${reportData.carePlanReviewsOverdue} overdue` : 'On track'],
      ['Supervisor visits (60-day)',  reportData.supervisorVisitsDue,  reportData.supervisorVisitsCompleted, reportData.supervisorVisitsDue === reportData.supervisorVisitsCompleted ? 'Compliant' : 'Action needed'],
      ['Physician summaries sent',    reportData.physicianSummariesDue, reportData.physicianSummariesSent,  reportData.physicianSummariesDue === reportData.physicianSummariesSent ? 'Compliant' : 'Pending'],
    ])}
  </div>

  ${section('5. Staffing & HR', narratives.staffing_summary, 'Rule 111-8-65-.10')}
  ${section('6. Quality Assurance & Risk', narratives.quality_assurance, 'Rule 111-8-65-.09')}

  ${isQ && narratives.clinical_record_audit ? section('Quarterly: Clinical Record Audit', narratives.clinical_record_audit, 'Rule 111-8-31-.07(8)(b)') : ''}
  ${isQ && narratives.personnel_meeting     ? section('Quarterly: Personnel Group Meeting', narratives.personnel_meeting, 'Rule 111-8-31-.07(3)(b)') : ''}

  <div style="margin-bottom:20px">
    <h2 style="font-size:13px;font-weight:700;color:#0A1628;margin:0 0 8px;border-bottom:1px solid #E8E0D0;padding-bottom:4px">
      ${isQ ? '9' : '7'}. Strategic Progress &amp; Next Period Action Plan
    </h2>
    <p style="font-size:12px;color:#333;line-height:1.6;white-space:pre-wrap">${strategicNotes || '(To be completed by administrator)'}</p>
  </div>

  <div style="border:1px solid #E8E0D0;border-radius:8px;padding:16px;margin-top:24px">
    <h2 style="font-size:13px;font-weight:700;color:#0A1628;margin:0 0 12px">Administrator Attestation</h2>
    <p style="font-size:12px;color:#333;line-height:1.6">I, the undersigned, attest that this report accurately reflects the operations of A Heart For Care for ${label}. All services provided were in accordance with the scope of our license and the individual Plans of Care for our clients.</p>
    <div style="display:flex;gap:40px;margin-top:20px">
      <div><div style="border-bottom:1px solid #333;width:200px;margin-bottom:4px"></div><p style="font-size:11px;color:#6B7A94;margin:0">Name</p></div>
      <div><div style="border-bottom:1px solid #333;width:200px;margin-bottom:4px"></div><p style="font-size:11px;color:#6B7A94;margin:0">Signature</p></div>
      <div><div style="border-bottom:1px solid #333;width:120px;margin-bottom:4px"></div><p style="font-size:11px;color:#6B7A94;margin:0">Date</p></div>
    </div>
  </div>

</div>
<script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); }</script>
</body></html>`;

    // FIX: use downloadCompliancePDF — not downloadPDF — to avoid double-wrapping
    downloadCompliancePDF(title, fullHtml);

    // Log to report_runs — use profile full_name if available
    supabase.from('report_runs').insert([{
      report_type:  modalType,
      period_start: reportData.periodStart,
      period_end:   reportData.periodEnd,
      period_label: isQ ? reportData.quarterLabel : reportData.periodLabel,
      generated_by: profile?.full_name || user?.email || 'unknown',
      generated_by_id: user?.id || null,
      status: 'complete',
    }]);
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const available = visibleReports.reduce((a, g) => a + g.items.filter(r => !r.coming).length, 0);
  const coming    = visibleReports.reduce((a, g) => a + g.items.filter(r =>  r.coming).length, 0);
  const urgent    = visibleReports.reduce((a, g) => a + g.items.filter(r =>  r.urgent).length, 0);

  return (
    <div>
      {error && (
        <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Reports available', value: available, tone: 'neutral' },
          { label: 'Coming soon',       value: coming,    tone: 'neutral' },
          { label: 'Action needed',     value: urgent,    tone: urgent > 0 ? 'alert' : 'neutral' },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />
        ))}
      </div>

      {visibleReports.map(group => (
        <div key={group.category} className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <div className="card-title">{group.category} Reports</div>
            <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 10, fontWeight: 500, background: group.tagBg, color: group.tagColor }}>{group.tag}</span>
          </div>
          {group.items.map(report => (
            <div key={report.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '0.5px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>
                  {report.name}
                  {report.urgent && <span style={{ fontSize: 10, marginLeft: 8, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '1px 6px', borderRadius: 10 }}>Action needed</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>{report.desc}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {report.coming ? (
                  <span style={{ fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '0.5px solid var(--border)', color: 'var(--text3)', background: 'var(--bg2)' }}>
                    Coming soon
                  </span>
                ) : report.isComplianceReport ? (
                  <button
                    onClick={() => { setModalType(report.type); setShowModal(true); setModalStep(1); setGenerateError(''); }}
                    style={{ padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'inherit', border: '0.5px solid var(--accent-border)',
                      background: 'var(--accent)', color: '#fff', transition: 'all 0.15s' }}>
                    Generate →
                  </button>
                ) : report.link ? (
                  <button
                    onClick={() => navigate(report.link)}
                    style={{ padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'inherit', border: '0.5px solid var(--accent-border)',
                      background: 'transparent', color: 'var(--accent)', transition: 'all 0.15s' }}>
                    Open page →
                  </button>
                ) : report.formats.map(fmt => {
                  const key  = `${report.name}-${fmt}`;
                  const busy = exporting === key;
                  const color  = report.urgent ? 'var(--danger)' : 'var(--accent)';
                  const bg     = report.urgent ? 'var(--danger-bg)' : 'var(--accent-bg)';
                  const border = report.urgent ? 'var(--danger-border)' : 'var(--accent-border)';
                  return (
                    <button key={fmt} onClick={() => handleExport(report, fmt)} disabled={!!exporting}
                      style={{ padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: busy ? 'wait' : 'pointer',
                        fontFamily: 'inherit', border: `0.5px solid ${border}`, background: busy ? color : 'transparent',
                        color: busy ? '#fff' : color, transition: 'all 0.15s', minWidth: 60, textAlign: 'center' }}>
                      {busy ? 'Generating...' : fmt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}

      <ReportHistory />

      {/* ── Compliance Report Modal ────────────────────────────────────────── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '40px 16px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: 680,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                  {modalType === 'monthly' ? 'Monthly' : 'Quarterly'} Compliance Report
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Rule 111-8-65 · A Heart For Care</div>
              </div>
              <button onClick={() => { setShowModal(false); setModalStep(1); }}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }}>×</button>
            </div>

            {/* Step 1 — Period selection */}
            {modalStep === 1 && (
              <div style={{ padding: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Select report period</div>

                {modalType === 'monthly' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Month</div>
                      <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                          background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i} value={i}>{format(new Date(2000, i, 1), 'MMMM')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Year</div>
                      <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                          background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
                        {[0, 1, 2].map(i => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
                      </select>
                    </div>
                  </div>
                )}

                {modalType === 'quarterly' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Quarter</div>
                      <select value={selectedQuarter} onChange={e => setSelectedQuarter(Number(e.target.value))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                          background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
                        {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Year</div>
                      <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                          background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
                        {[0, 1, 2].map(i => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
                      </select>
                    </div>
                  </div>
                )}

                {/* Quarterly manual fields */}
                {modalType === 'quarterly' && (
                  <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                      Quarterly compliance data (Rule 111-8-31-.07)
                    </div>
                    {[
                      { label: 'Clinical record audit — number of records reviewed', val: auditRecordsReviewed, set: setAuditRecordsReviewed, type: 'number' },
                      { label: 'Audit findings and corrective actions', val: auditFindings, set: setAuditFindings, type: 'textarea' },
                      { label: 'Personnel group meeting date', val: meetingDate, set: setMeetingDate, type: 'date' },
                      { label: 'Meeting attendees', val: meetingAttendees, set: setMeetingAttendees, type: 'text' },
                      { label: 'Items reviewed at meeting', val: meetingItems, set: setMeetingItems, type: 'textarea' },
                    ].map(({ label, val, set, type }) => (
                      <div key={label} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{label}</div>
                        {type === 'textarea'
                          ? <textarea value={val} onChange={e => set(e.target.value)} rows={3}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                                background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, resize: 'vertical', fontFamily: 'inherit' }} />
                          : <input type={type} value={val} onChange={e => set(e.target.value)}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                                background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }} />
                        }
                      </div>
                    ))}
                  </div>
                )}

                {generateError && (
                  <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)',
                    borderRadius: 7, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>
                    {generateError}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setShowModal(false)} className="btn">Cancel</button>
                  <button onClick={generateComplianceReport} className="btn btn-primary" disabled={generating}>
                    Generate report →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 — Loading */}
            {modalStep === 2 && (
              <div style={{ padding: 32 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 20, textAlign: 'center' }}>
                  Building your compliance report…
                </div>
                {LOADING_STEPS.map(step => {
                  const done   = loadingDone.includes(step);
                  const active = loadingStep === step && !done;
                  return (
                    <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 0', borderBottom: '0.5px solid var(--border)', fontSize: 13 }}>
                      <span style={{ fontSize: 16, width: 20 }}>
                        {done ? '✅' : active ? '⏳' : '⬜'}
                      </span>
                      <span style={{ color: done ? 'var(--text)' : active ? 'var(--accent)' : 'var(--text3)' }}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Step 3 — Preview */}
            {modalStep === 3 && reportData && narratives && (
              <div style={{ padding: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  Report preview — {modalType === 'quarterly' ? reportData.quarterLabel : reportData.periodLabel}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                  Review the AI-generated sections below. Edit strategic notes before downloading.
                </div>

                {/* Key metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'Active patients',     value: reportData.activePatients },
                    { label: 'Hours delivered',     value: reportData.totalHoursDelivered },
                    { label: 'Missed visits',        value: reportData.missedVisitsCount },
                    { label: 'Supervisor visits',   value: `${reportData.supervisorVisitsCompleted}/${reportData.supervisorVisitsDue}` },
                    { label: 'Physician summaries', value: `${reportData.physicianSummariesSent} sent` },
                    { label: 'Care plan reviews',   value: `${reportData.carePlanReviewsCompliant} on time` },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: 'var(--accent-bg)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)' }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Narrative preview */}
                {[
                  { key: 'executive_summary',    label: '§1 Executive summary' },
                  { key: 'service_delivery',     label: '§3.1 Service delivery' },
                  { key: 'clinical_compliance',  label: '§4 Clinical compliance' },
                  { key: 'financial_summary',    label: '§3.3 Financial accountability' },
                  { key: 'quality_assurance',    label: '§6 Quality assurance' },
                  { key: 'staffing_summary',     label: '§5 Staffing & HR' },
                  ...(modalType === 'quarterly' ? [
                    { key: 'clinical_record_audit', label: '§4.4 Clinical record audit' },
                    { key: 'personnel_meeting',     label: '§4.5 Personnel meeting' },
                  ] : []),
                ].map(({ key, label }) => narratives[key] ? (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase',
                      letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6,
                      background: 'var(--bg2)', borderRadius: 6, padding: '10px 12px' }}>
                      {narratives[key]}
                    </div>
                  </div>
                ) : null)}

                {/* Strategic notes */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase',
                    letterSpacing: '0.05em', marginBottom: 4 }}>§8 Strategic progress &amp; next period action plan</div>
                  <textarea
                    value={strategicNotes}
                    onChange={e => setStrategicNotes(e.target.value)}
                    rows={5}
                    placeholder="Enter priorities, challenges, and action items for next period…"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 7, border: '1px solid var(--border)',
                      background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setModalStep(1)} className="btn">← Back</button>
                  <button onClick={downloadComplianceReportPDF} className="btn btn-primary">Download PDF</button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
}
